import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ToggleLeft } from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Mono,
  PageHeader,
  Text,
} from "@lagoon/ui";

import { api, type Me, type PlatformFlag } from "../api.js";
import { ReasonAction } from "../components/reason-action.js";
import { editableFlagValue, flagKind, formatFlagValue, parseFlagValue } from "../flags.js";

/**
 * The platform kill switches (LGN-125, audit M9).
 *
 * The screening queue's empty state told an operator to "check
 * `screening.engine` on the Flags screen" and there was no Flags screen: the
 * routes existed, the console never called them, and every switch in
 * `platform_flags` was a `psql` or `curl` operation. `screening.engine` is the
 * one that makes this urgent, because turning the scanner on (LGN-79,
 * `docs/runbooks/clamav.md`) is a step Tech Ops is supposed to be able to take.
 *
 * A flag is edited in the shape it already holds, never coerced: see
 * `../flags.ts` for why that matters for `screening.engine` in particular.
 *
 * Every write is a `ReasonAction`, like every other mutation on this surface,
 * and `setPlatformFlag` writes the `flag.toggle` audit row in the same
 * transaction as the change.
 */
export function FlagsScreen({ me }: { me: Me }): React.JSX.Element {
  const queryClient = useQueryClient();
  const flags = useQuery({ queryKey: ["flags"], queryFn: () => api.flags() });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["flags"] });
    // A flag is read by other surfaces, and the console's own counts are the
    // cheapest signal that one of them noticed.
    void queryClient.invalidateQueries({ queryKey: ["health"] });
  }, [queryClient]);

  const columns = useFlagColumns({ canAdminister: me.can.administer, onDone: invalidate });

  return (
    <div className="lg:flex lg:flex-col lg:gap-8">
      <PageHeader
        eyebrow="Record"
        title="Platform flags"
        description="The switches that govern the whole stack, and who last moved each one."
      />

      <Alert tone="warning">
        <AlertTitle>These are stack-wide, and they take effect immediately</AlertTitle>
        <AlertDescription>
          Nothing here is scoped to a tenant. <Mono>screening.engine</Mono> decides whether uploaded
          files are scanned at all, and <Mono>screening.enforce</Mono> decides whether an unscanned
          release may publish. <Mono>docs/runbooks/clamav.md</Mono> has the order to move them in.
        </AlertDescription>
      </Alert>

      {!me.can.administer && (
        <Alert tone="info">
          <AlertTitle>Read-only for your account</AlertTitle>
          <AlertDescription>
            Changing a flag is an administrator action. You can see what every switch is set to and
            who last moved it.
          </AlertDescription>
        </Alert>
      )}

      {flags.error && (
        <Text size="sm" tone="danger" role="alert">
          {flags.error.message}
        </Text>
      )}

      <DataTable
        columns={columns}
        data={flags.data?.flags ?? []}
        loading={flags.isPending}
        caption="Every platform flag and its current value"
        empty={
          <EmptyState
            icon={<ToggleLeft />}
            title="No flags are configured"
            description="The table is seeded by migration, so an empty one means this stack has not been migrated."
            glow={false}
          />
        }
      />
    </div>
  );
}

function useFlagColumns({
  canAdminister,
  onDone,
}: {
  canAdminister: boolean;
  onDone: () => void;
}): DataTableColumn<PlatformFlag>[] {
  return useMemo(() => {
    const columns: DataTableColumn<PlatformFlag>[] = [
      {
        accessorKey: "key",
        header: "Flag",
        cell: (info) => (
          <div className="lg:flex lg:flex-col lg:gap-1">
            <Mono className="lg:text-strong">{info.row.original.key}</Mono>
            {info.row.original.description !== null && (
              <Text size="caption" tone="subtle">
                {info.row.original.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        id: "value",
        header: "Value",
        cell: (info) => <ValueBadge value={info.row.original.value} />,
      },
      {
        accessorKey: "updatedAt",
        header: "Last moved",
        sortFn: "datetime",
        cell: (info) => (
          <div className="lg:flex lg:flex-col">
            <Mono className="lg:text-caption lg:text-muted-foreground">
              {new Date(info.row.original.updatedAt).toLocaleString()}
            </Mono>
            <Text size="caption" tone="subtle">
              {info.row.original.updatedBy ?? "migration"}
            </Text>
          </div>
        ),
      },
    ];

    // Absent for an operator or an auditor rather than disabled, which is how
    // every other screen here says "this account is not for that".
    if (canAdminister) {
      columns.push({
        id: "actions",
        header: "Actions",
        cell: (info) => <FlagActions flag={info.row.original} onDone={onDone} />,
      });
    }

    return columns;
  }, [canAdminister, onDone]);
}

/**
 * The value, coloured only where a colour means something.
 *
 * `true` is not "good" and `false` is not "bad": `signup.open` true and
 * `screening.enforce` false are both postures somebody chose. So a boolean is
 * neutral with its word on it, and the only tint is the one for a flag holding
 * nothing at all.
 */
function ValueBadge({ value }: { value: unknown }): React.JSX.Element {
  const text = formatFlagValue(value);
  return (
    <Badge tone={value === null || value === undefined ? "warning" : "neutral"}>
      {text === "" ? "(empty)" : text}
    </Badge>
  );
}

/**
 * One flag's write.
 *
 * A boolean gets the flip it obviously wants, with the value it will become in
 * the button. Everything else gets a field opened on its current value, so
 * moving `screening.engine` from `none` to `clamav` is a word, not a guess at
 * JSON.
 *
 * Every one of these is `danger`, in both directions. `true` is not "safe" and
 * `false` is not "risky": `signup.open` on and `screening.enforce` off are both
 * postures somebody chose deliberately, and painting one direction calm would
 * be a claim this screen is not in a position to make. What they have in common
 * is that they change the behaviour of the whole stack, which is what the
 * outline is for.
 */
function FlagActions({
  flag,
  onDone,
}: {
  flag: PlatformFlag;
  onDone: () => void;
}): React.JSX.Element {
  const kind = flagKind(flag.value);

  if (kind === "boolean") {
    const next = flag.value !== true;
    return (
      <ReasonAction
        label={next ? "Turn on" : "Turn off"}
        compact
        danger
        hint={`Sets ${flag.key} to ${String(next)} for the whole stack, from the next read.`}
        onSubmit={async (reason) => {
          await api.setFlag(flag.key, next, reason);
          onDone();
        }}
      />
    );
  }

  return (
    <ReasonAction
      label="Change"
      compact
      danger
      hint={`Sets ${flag.key} for the whole stack, from the next read.`}
      field={{
        label: "New value",
        initialValue: editableFlagValue(flag.value),
        ...(flag.key === "screening.engine" ? { hint: "none or clamav" } : {}),
        validate: (text) => {
          const parsed = parseFlagValue(kind, text);
          return parsed.ok ? null : parsed.problem;
        },
      }}
      onSubmit={async (reason, text) => {
        const parsed = parseFlagValue(kind, text ?? "");
        // Refused rather than sent: `validate` has already said no, and a
        // silent write of a value nothing can read is the failure this screen
        // exists to prevent.
        if (!parsed.ok) throw new Error(parsed.problem);
        await api.setFlag(flag.key, parsed.value, reason);
        onDone();
      }}
    />
  );
}
